import { useEffect, useMemo, useState } from "react";

import { useProjects } from "../../queries/project";
import { useTasks } from "../../queries/task";
import { useLocale } from "../../stores/locale";
import { padLeft } from "../../utils/pad-left";
import { AutoComplete } from "../auto-complete";
import { Button } from "../button";
import { Input } from "../input";

export type TimeForm = {
  project: string;
  task: string;
  description: string;
  startTime: string;
  endTime: string;
};

export function TimeForm({
  value,
  onSubmit,
  onCancel,
  onChange,
  initChange = false,
  isValid = true,
  loading = false,
  submitting = false,
  extraButtons,
}: {
  value?: TimeForm;
  onSubmit: (time: TimeForm) => void;
  onCancel: () => void;
  onChange?: (time: TimeForm) => void;
  initChange?: boolean;
  isValid?: boolean;
  loading?: boolean;
  submitting?: boolean;
  extraButtons?: React.ReactNode;
}) {
  const t = useLocale((state) => state.locale);

  const [form, setForm] = useState<TimeForm>(
    value
      ? { ...value }
      : {
          project: "",
          task: "",
          description: "",
          startTime: "",
          endTime: "",
        },
  );

  useEffect(() => {
    if (initChange && onChange) {
      onChange(form);
    }
  }, [initChange, onChange, form]);

  const isLoading = useMemo(() => loading || submitting, [loading, submitting]);

  return (
    <form
      className="flex flex-col gap-4 flex-grow"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(form);
      }}
    >
      <div className="flex gap-2">
        <div className="w-1/2">
          <ProjectSelector
            disabled={isLoading}
            value={form.project}
            onChange={(project) => {
              const change = { ...form, project, task: "" };
              setForm(change);
              if (onChange) {
                onChange(change);
              }
            }}
          />
        </div>
        <div className="w-1/2">
          <TaskSelector
            disabled={form.project === "" || isLoading}
            project={form.project}
            value={form.task}
            onChange={(task) => {
              setForm({ ...form, task });
              if (onChange) {
                onChange({ ...form, task });
              }
            }}
          />
        </div>
      </div>
      <div>
        <Input
          disabled={isLoading}
          label={t.time.description}
          rows={10}
          type="textarea"
          value={form.description}
          onChange={(event) => {
            setForm({ ...form, description: event.target.value });
            if (onChange) {
              onChange(form);
            }
          }}
        />
      </div>
      <div className="flex gap-5 w-2/3 sm:w-1/3">
        <Input
          className="w-1/2"
          disabled={isLoading}
          label={t.time.startTime}
          placeholder="(now)"
          type="text"
          value={form.startTime}
          onBlur={() => {
            setForm({ ...form, startTime: parseTime(form.startTime) });
            if (onChange) {
              onChange({ ...form, startTime: parseTime(form.startTime) });
            }
          }}
          onChange={(event) => {
            setForm({ ...form, startTime: event.target.value });
            if (onChange) {
              onChange({
                ...form,
                startTime: parseTime(event.target.value),
              });
            }
          }}
        />
        <Input
          className="w-1/2"
          disabled={isLoading}
          label={t.time.endTime}
          type="text"
          value={form.endTime}
          onBlur={() => setForm({ ...form, endTime: parseTime(form.endTime) })}
          onChange={(event) => {
            setForm({ ...form, endTime: event.target.value });
            if (onChange) {
              onChange({
                ...form,
                endTime: parseTime(event.target.value),
              });
            }
          }}
        />
      </div>
      <div className="flex justify-end gap-2 mt-auto">
        {extraButtons}
        <Button
          disabled={isLoading}
          severity="secondary"
          type="button"
          onClick={() => {
            onCancel();
          }}
        >
          {t.cancel}
        </Button>
        <Button
          disabled={!isValid || isLoading}
          loading={submitting}
          type="submit"
        >
          {t.time.submit}
        </Button>
      </div>
    </form>
  );
}

function ProjectSelector({
  value,
  onChange,
  disabled = false,
}: {
  value: string;
  onChange: (project: string) => void;
  disabled?: boolean;
}) {
  const t = useLocale((state) => state.locale);

  const { data: projects } = useProjects();

  return (
    <AutoComplete
      dropdown
      disabled={disabled}
      id="project"
      label={t.time.project}
      options={projects ?? []}
      value={value}
      onChange={(value) => onChange(value)}
    />
  );
}

function TaskSelector({
  disabled = false,
  value,
  project,
  onChange,
}: {
  disabled?: boolean;
  value: string;
  project: string;
  onChange: (task: string) => void;
}) {
  const t = useLocale((state) => state.locale);

  const { data: tasks } = useTasks(project);

  return (
    <AutoComplete
      dropdown
      disabled={disabled}
      id="task"
      label={t.time.task}
      options={tasks?.map((task) => task.name) ?? []}
      value={value}
      onChange={onChange}
    />
  );
}

function parseTime(input: string): string {
  input = input.trim();
  if (input === "") return "";

  const split = input.split(":");
  if (split.length === 2) {
    const hour = parseInt(split[0] ?? "0");
    const minute = parseInt(split[1] ?? "0");

    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return `${hour}:${padLeft(minute, 2)}`;
    }
  } else if (split.length === 1) {
    if (input.length <= 2) {
      const hour = parseInt(input);

      if (hour >= 0 && hour <= 23) {
        return `${hour}:00`;
      }
    } else if (input.length <= 4) {
      const hour = parseInt(input.slice(0, 2));
      const minute = parseInt(input.slice(2));

      if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
        return `${hour}:${padLeft(minute, 2)}`;
      }
    }
  }

  return "";
}
